import { CapacitorCookies } from '@capacitor/core'

const AUTH_COOKIE_NAME = 'authInfo'

export type AuthInfoType = {
  accessToken: string
  refreshToken?: string
}

export class AuthCookieService {
  /**
   * Store authentication information to cookie
   */
  public static async storeAuthInfo(authInfo: AuthInfoType, persistent: boolean): Promise<void> {
    const defaultDuration = 1000 * 60 * 60 * 24 * 62 // 62 days in milliseconds
    const duration = persistent ? defaultDuration : 0
    await AuthCookieService.store(AUTH_COOKIE_NAME, authInfo, duration)
  }

  /**
   * Get authentication information from cookie
   */
  public static async getAuthInfo(): Promise<AuthInfoType | undefined> {
    return await AuthCookieService.get(AUTH_COOKIE_NAME)
  }

  /**
   * Clear authentication information cookie
   */
  public static async clearAuthInfo(): Promise<void> {
    const d: Date = new Date()
    d.setTime(0)

    await CapacitorCookies.setCookie({
      key: AUTH_COOKIE_NAME,
      value: '',
      expires: d.toUTCString(),
      path: '/',
    })
  }

  /**
   * Generic value store to cookie
   */
  private static async store(cookieName: string, value: any, duration: number): Promise<void> {
    const cookieValue = JSON.stringify(value)

    let expires = ''
    if (duration) {
      const d = new Date()
      d.setTime(d.getTime() + duration)
      expires = d.toUTCString()
    }

    await CapacitorCookies.setCookie({
      key: cookieName,
      value: cookieValue,
      expires: expires,
      path: '/',
    })
  }

  /**
   * Generic value get from cookie
   */
  private static async get(cookieName: string): Promise<any> {
    try {
      const cookies = await CapacitorCookies.getCookies()
      const value = cookies[cookieName]
      if (value) {
        const decodedValue = decodeURIComponent(value)
        try {
          if (decodedValue.startsWith('"') && decodedValue.endsWith('"')) {
            const cleanedValue = decodedValue.slice(1, -1).replace(/\\"/g, '"')
            return JSON.parse(cleanedValue)
          }
          return JSON.parse(decodedValue)
        } catch (parseError) {
          try {
            const cleanedValue = decodedValue.replace(/\\/g, '')
            return JSON.parse(cleanedValue)
          } catch (fallbackError) {
            console.error('Fallback parsing also failed:', fallbackError)
          }
        }
      }
    } catch (error) {
      console.error('Error getting cookie:', error)
    }
    return undefined
  }
}
